import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, getTranslation } from '../i18n/translations';

const LanguageContext = createContext();

export const LANGUAGES = [
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

// Country to language mapping
const COUNTRY_LANGUAGE_MAP = {
  'GR': 'el', // Greece
  'CY': 'el', // Cyprus
  'DE': 'de', // Germany
  'AT': 'de', // Austria
  'CH': 'de', // Switzerland (German)
  'IT': 'it', // Italy
  'ES': 'es', // Spain
  'MX': 'es', // Mexico
  'AR': 'es', // Argentina
  // All other countries default to English
};

// Greek-speaking countries
const GREEK_COUNTRIES = ['GR', 'CY'];

// Detect language from IP/Geolocation
const detectLanguageFromIP = async () => {
  try {
    // Use free IP geolocation API
    const response = await fetch('https://ipapi.co/json/', { 
      timeout: 3000,
      cache: 'no-store'
    });
    
    if (!response.ok) {
      console.log('🌐 IP detection failed, using browser language');
      return null;
    }
    
    const data = await response.json();
    const countryCode = data.country_code || data.country;
    
    console.log(`🌐 IP Location detected: ${data.country_name} (${countryCode})`);
    
    // Check if it's a Greek-speaking country
    if (GREEK_COUNTRIES.includes(countryCode)) {
      return 'el';
    }
    
    // Check country-language mapping
    if (COUNTRY_LANGUAGE_MAP[countryCode]) {
      return COUNTRY_LANGUAGE_MAP[countryCode];
    }
    
    // Default to English for all other countries
    return 'en';
  } catch (error) {
    console.log('🌐 IP detection error, using browser language:', error.message);
    return null;
  }
};

// Detect browser language and map to supported language
const detectBrowserLanguage = () => {
  // Get browser language (e.g., 'en-US', 'el', 'de-DE')
  const browserLang = navigator.language || navigator.userLanguage || 'el';
  
  // Extract the main language code (e.g., 'en' from 'en-US')
  const mainLang = browserLang.split('-')[0].toLowerCase();
  
  // Check if we support this language
  const supportedCodes = LANGUAGES.map(l => l.code);
  
  if (supportedCodes.includes(mainLang)) {
    return mainLang;
  }
  
  // Default to English for international users (not Greek)
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [currentLang, setCurrentLang] = useState('el');
  const [isDetecting, setIsDetecting] = useState(true);

  // Load language preference: URL param > localStorage > IP detection > browser detection
  useEffect(() => {
    const detectLanguage = async () => {
      setIsDetecting(true);
      
      // 1. First check URL parameter ?lang=xx
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');
      
      if (urlLang && translations[urlLang]) {
        // URL parameter takes priority
        setCurrentLang(urlLang);
        localStorage.setItem('preferredLanguage', urlLang);
        console.log(`🌐 Language set from URL parameter: ${urlLang}`);
        setIsDetecting(false);
        return;
      }
      
      // 2. Check localStorage for saved preference
      const savedLang = localStorage.getItem('preferredLanguage');
      
      if (savedLang && translations[savedLang]) {
        // User has a saved preference - use it
        setCurrentLang(savedLang);
        console.log(`🌐 Language loaded from localStorage: ${savedLang}`);
        setIsDetecting(false);
        return;
      }
      
      // 3. No saved preference - try IP detection first (for non-Greek IPs)
      const ipLang = await detectLanguageFromIP();
      
      if (ipLang) {
        setCurrentLang(ipLang);
        localStorage.setItem('preferredLanguage', ipLang);
        console.log(`🌐 Language set from IP detection: ${ipLang}`);
        setIsDetecting(false);
        return;
      }
      
      // 4. Fallback to browser language detection
      const browserLang = detectBrowserLanguage();
      setCurrentLang(browserLang);
      localStorage.setItem('preferredLanguage', browserLang);
      console.log(`🌐 Language set from browser detection: ${browserLang}`);
      setIsDetecting(false);
    };
    
    detectLanguage();
  }, []);

  // Update document lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = currentLang;
    
    // Update meta description based on language (dynamic SEO)
    const metaDesc = document.querySelector('meta[name="description"]:not([lang])');
    if (metaDesc) {
      const descriptions = {
        el: 'Η πρώτη εφαρμογή που μετατρέπει το ραδιόφωνο σε μετρήσιμο media channel: campaign performance, audience data, playlist intelligence και daily on-air content. Radio finally measured.',
        en: 'The first app that turns radio into a measurable media channel: campaign performance tracking, audience data analytics, playlist intelligence and daily on-air content. Radio finally measured.',
        de: 'Die erste App, die Radio in einen messbaren Medienkanal verwandelt: Kampagnen-Performance, Zielgruppendaten, Playlist-Intelligence und tägliche On-Air-Inhalte. Radio endlich messbar.',
        it: 'La prima app che trasforma la radio in un canale media misurabile: performance delle campagne, dati del pubblico, playlist intelligence e contenuti on-air quotidiani. Radio finalmente misurabile.',
        es: 'La primera aplicación que convierte la radio en un canal de medios medible: rendimiento de campañas, datos de audiencia, inteligencia de playlists y contenido on-air diario. Radio finalmente medible.'
      };
      metaDesc.setAttribute('content', descriptions[currentLang] || descriptions.en);
    }
  }, [currentLang]);

  // Save language preference when it changes
  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setCurrentLang(langCode);
      localStorage.setItem('preferredLanguage', langCode);
    }
  };

  // Translation function with parameter support
  const t = (key, params = {}) => {
    let translation = getTranslation(currentLang, key);
    
    // Replace parameters like {{month}} with actual values
    if (params && typeof translation === 'string') {
      Object.keys(params).forEach(param => {
        translation = translation.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
      });
    }
    
    return translation;
  };

  // Get current language object
  const getCurrentLanguage = () => {
    return LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];
  };

  const value = {
    currentLang,
    changeLanguage,
    t,
    getCurrentLanguage,
    languages: LANGUAGES,
    isDetecting,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
